/**
 * Handler centralizado para processamento de dados de bootstrap.
 * Unifica a lógica de processamento de bootstrap entre DockerUI e DockerTools.
 */

import { TokenService } from "../tokenService";
import { setShortSsoToken, setBaseBearer } from "../toolsSso";
import { terminalLog, sendToTerminal } from "../terminalLogger";
import { scheduleTokenRefresh } from "../auth";
import {
	isValidPermissionId,
	mapPermissionIdToRole,
} from "../permissionMapper";
import { reconcileAuthState } from "../auth/reconcileAuthState";

/**
 * Extrai o campo `exp` do payload JWT e converte para ISO string.
 * @param token - Token JWT
 * @returns ISO date string ou undefined se não encontrar exp
 */
function extractExpiresAtFromJwt(token: string): string | undefined {
	try {
		const parts = token.split(".");
		if (parts.length < 2) return undefined;
		const payload = parts[1];
		const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		const obj = JSON.parse(json);
		if (typeof obj?.exp === "number") {
			return new Date(obj.exp * 1000).toISOString();
		}
	} catch {
		// Silencioso - token inválido ou sem exp
	}
	return undefined;
}

type BootstrapJwtClaims = {
	exp?: number;
	sub?: string | number;
	user?: string;
	user_id?: number;
	user_nome?: string;
	perm_id?: number;
};

function decodeBootstrapJwtClaims(token: string): BootstrapJwtClaims | null {
	try {
		const parts = token.split(".");
		if (parts.length < 2) return null;
		const payload = parts[1];
		const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
		return JSON.parse(json) as BootstrapJwtClaims;
	} catch {
		return null;
	}
}

function buildFallbackUser(
	accessToken: string,
	user?: BootstrapData["user"],
): { id: number; nome: string; login: string; isadmin?: boolean } | undefined {
	const claims = decodeBootstrapJwtClaims(accessToken);
	if (!user && !claims) return undefined;

	const rawId = user?.id ?? claims?.user_id ?? claims?.sub;
	const parsedId = typeof rawId === "string" ? Number(rawId) : rawId;
	const userId =
		typeof parsedId === "number" && Number.isFinite(parsedId) ? parsedId : 0;

	const nome =
		user?.nome || claims?.user_nome || claims?.user || user?.login || "";

	const login = user?.login || claims?.user || "";

	const inferredPermId = claims?.perm_id;
	const inferredIsAdmin =
		typeof inferredPermId === "number" ? inferredPermId === 2 : undefined;

	return {
		id: userId,
		nome,
		login,
		isadmin: user?.isadmin ?? inferredIsAdmin,
	};
}

function buildFallbackPermissions(
	accessToken: string,
	permissoes?: any[],
): any[] {
	if (Array.isArray(permissoes) && permissoes.length > 0) return permissoes;

	const claims = decodeBootstrapJwtClaims(accessToken);
	const permId = claims?.perm_id;
	if (typeof permId !== "number" || !isValidPermissionId(permId)) return [];

	const role = mapPermissionIdToRole(permId);
	return [
		{
			perm_id: permId,
			perm_descricao: role,
			perm_path: `/${role}`,
		},
	];
}

function buildFallbackPermissionsConfig(
	accessToken: string,
	permissionsConfig?: any[],
): any[] {
	if (Array.isArray(permissionsConfig) && permissionsConfig.length > 0)
		return permissionsConfig;

	const claims = decodeBootstrapJwtClaims(accessToken);
	const permId = claims?.perm_id;
	if (typeof permId !== "number" || !isValidPermissionId(permId)) return [];

	return [
		{
			perm_id: permId,
			role_name: mapPermissionIdToRole(permId),
			features: {},
		},
	];
}

export interface BootstrapData {
	accessToken?: string;
	sessionToken?: string;
	user?: { id: number; nome: string; login: string; isadmin?: boolean };
	permissoes?: any[];
	permissionsConfig?: any[];
	isAdmin?: boolean;
}

export type BootstrapSource = "fragment" | "message" | "storage";

export interface BootstrapResult {
	success: boolean;
	error?: string;
}

type AppliedBootstrapCycle = {
	accessToken: string;
	sessionToken: string | null;
	sessionEpoch: number;
	tokenVersion: number;
};

let lastAppliedBootstrapCycle: AppliedBootstrapCycle | null = null;
const inflightBootstrapCycles = new Map<string, Promise<BootstrapResult>>();

/**
 * Processa dados de bootstrap de forma unificada.
 * Normaliza e salva tokens, dados do usuário e configurações.
 *
 * @param data - Dados de bootstrap recebidos
 * @param source - Origem dos dados (fragment, message, storage)
 * @returns Resultado do processamento
 */
export async function handleBootstrapData(
	data: BootstrapData,
	source: BootstrapSource = "message",
): Promise<BootstrapResult> {
	try {
		const {
			accessToken,
			user,
			permissoes,
			permissionsConfig,
			isAdmin,
			sessionToken,
		} = data;

		// [DEBUG-EMBEDDED] Log do que chega no bootstrap
		sendToTerminal("🔎 [BOOTSTRAP] Dados recebidos do Launcher", {
			source,
			hasAccessToken: !!accessToken,
			hasUser: !!user,
			userId: user?.id,
			userName: user?.nome,
			hasPermissoes: Array.isArray(permissoes) && permissoes.length > 0,
			permissoesCount: Array.isArray(permissoes) ? permissoes.length : 0,
			hasPermissionsConfig:
				Array.isArray(permissionsConfig) && permissionsConfig.length > 0,
			permissionsConfigCount: Array.isArray(permissionsConfig)
				? permissionsConfig.length
				: 0,
			isAdmin,
			hasSessionToken: !!sessionToken,
		});

		// Validar que há pelo menos accessToken
		if (!accessToken) {
			const errorMsg = "Bootstrap sem accessToken";
			terminalLog.warn("bootstrap-handler:no-access-token", { source });
			return { success: false, error: errorMsg };
		}

		const sessionEpoch = Number((window as any).sessionEpoch || 0);
		const tokenVersion = Number((window as any).tokenVersion || 0);
		const currentSessionToken = sessionToken ?? ((window as any).sessionToken || null);
		const bootstrapKey = `${accessToken}::${currentSessionToken ?? "null"}::${sessionEpoch}::${tokenVersion}`;

		if (
			lastAppliedBootstrapCycle &&
			lastAppliedBootstrapCycle.accessToken === accessToken &&
			lastAppliedBootstrapCycle.sessionToken === currentSessionToken &&
			lastAppliedBootstrapCycle.sessionEpoch === sessionEpoch &&
			lastAppliedBootstrapCycle.tokenVersion === tokenVersion
		) {
			sendToTerminal("⏭️ [BOOTSTRAP] Bootstrap ignorado (ciclo já aplicado)", {
				source,
				sessionEpoch,
				tokenVersion,
			});
			return { success: true };
		}

		const inflight = inflightBootstrapCycles.get(bootstrapKey);
		if (inflight) {
			sendToTerminal("⏳ [BOOTSTRAP] Bootstrap já em processamento para este ciclo", {
				source,
				sessionEpoch,
				tokenVersion,
			});
			return await inflight;
		}

		const bootstrapPromise = (async (): Promise<BootstrapResult> => {
			// Salvar token SSO
			TokenService.setTokens(accessToken);

			// Agendar refresh automático antes de expirar (independente do Launcher)
			scheduleTokenRefresh();

			// Extrair expiresAt do JWT para agendar auto-refresh corretamente
			const expiresAt = extractExpiresAtFromJwt(accessToken);
			setShortSsoToken(accessToken, expiresAt);
			setBaseBearer(accessToken);

			if (expiresAt) {
				terminalLog.debug("bootstrap-handler:sso-expires-at", {
					expiresAt,
					source,
				});
			}

			// Salvar sessionToken se fornecido
			if (sessionToken) {
				try {
					(window as any).sessionToken = sessionToken;
					terminalLog.debug("bootstrap-handler:session-token-saved", { source });
				} catch (err) {
					sendToTerminal("❌ [BOOTSTRAP] Erro ao salvar session token", {
						error: err instanceof Error ? err.message : String(err),
						source,
					});
				}
			}

			const normalizedUser = buildFallbackUser(accessToken, user);
			const normalizedPermissions = buildFallbackPermissions(
				accessToken,
				permissoes,
			);
			const normalizedPermissionsConfig = buildFallbackPermissionsConfig(
				accessToken,
				permissionsConfig,
			);

			// [DEBUG-EMBEDDED] Log após normalização — features: {} vazio = menu vai ficar vazio
			const firstConfig = normalizedPermissionsConfig[0];
			const featuresCount = firstConfig
				? Object.keys(firstConfig.features || {}).length
				: 0;
			sendToTerminal("🔎 [BOOTSTRAP] Após normalização", {
				source,
				permId: firstConfig?.perm_id,
				roleName: firstConfig?.role_name,
				featuresCount,
				usedFallback:
					!Array.isArray(permissionsConfig) || permissionsConfig.length === 0,
				featuresEmpty: featuresCount === 0,
			});
			const inferredPermId = normalizedPermissionsConfig[0]?.perm_id;
			const inferredIsAdmin =
				isAdmin ??
				normalizedUser?.isadmin ??
				(typeof inferredPermId === "number" ? inferredPermId === 2 : false);

			// Normalizar e salvar dados do usuário
			if (
				normalizedUser ||
				normalizedPermissions.length > 0 ||
				normalizedPermissionsConfig.length > 0
			) {
				const normalized = {
					data: {
						accessToken,
						usuario: normalizedUser
							? {
								id: normalizedUser.id,
								nome: normalizedUser.nome,
								login: normalizedUser.login,
							}
							: undefined,
						permissions: normalizedPermissions,
						permissionsConfig: normalizedPermissionsConfig,
						isAdmin: inferredIsAdmin,
					},
				};

				TokenService.setUserData(normalized);

				terminalLog.debug("bootstrap-handler:user-data-saved", {
					source,
					hasUser: !!normalizedUser,
					hasPermissions: normalizedPermissions.length > 0,
					hasConfig: normalizedPermissionsConfig.length > 0,
					inferredPermId,
					inferredIsAdmin,
					usedJwtFallback:
						!permissoes ||
						permissoes.length === 0 ||
						!permissionsConfig ||
						permissionsConfig.length === 0,
				});
			}

			// Se o bootstrap não trouxe permissionsConfig real (fluxo embedded via pipe),
			// buscar do backend via /auth/me para preencher as features
			const hadRealConfig =
				Array.isArray(permissionsConfig) &&
				permissionsConfig.length > 0 &&
				permissionsConfig.some(
					(c: any) => c.features && Object.keys(c.features).length > 0,
				);

			if (!hadRealConfig && accessToken) {
				await fetchPermissionsFromMe(accessToken);
			}

			lastAppliedBootstrapCycle = {
				accessToken,
				sessionToken: currentSessionToken,
				sessionEpoch,
				tokenVersion,
			};

			return { success: true };
		})();

		inflightBootstrapCycles.set(bootstrapKey, bootstrapPromise);
		try {
			return await bootstrapPromise;
		} finally {
			inflightBootstrapCycles.delete(bootstrapKey);
		}
	} catch (error) {
		const errorMsg = error instanceof Error ? error.message : String(error);
		sendToTerminal("❌ [BOOTSTRAP] Erro ao processar bootstrap", {
			error: errorMsg,
			source,
			stack: error instanceof Error ? error.stack : undefined,
		});
		return { success: false, error: errorMsg };
	}
}

/**
 * Busca permissionsConfig via GET /auth/me e atualiza o storage.
 * Chamado automaticamente quando o bootstrap (pipe do launcher) não inclui permissionsConfig.
 */
async function fetchPermissionsFromMe(accessToken: string): Promise<void> {
	sendToTerminal(
		"🔎 [BOOTSTRAP] permissionsConfig vazio — buscando via /auth/me",
		{},
	);

	await reconcileAuthState(accessToken, {
		reason: "bootstrap:missing-permissions-config",
		timeoutMs: 5000,
	});
}
